import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';

import {
	getSplit,
	getSettlement,
	resolveSettlement,
	unsettleSettlement,
	updateParticipant
} from '$lib/api/splits';

import { Button } from '$lib/components/ui/button';
import * as Card from '$lib/components/ui/card';
import { addToast } from '$lib/stores/toastStore.svelte';
import { route, navigate } from '$lib/router';
import SplitPageHeader from '$lib/components/ui/split-page-header/SplitPageHeader.svelte';
import { getCachedSplitName, setCachedSplitName } from '$lib/stores/splitTitleCache';

var root_2 = $.from_html(`<div class="flex flex-col items-center justify-center py-12 space-y-4"><svg class="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> <p class="text-muted-foreground">Loading settlement...</p></div>`);
var root_6 = $.from_html(`<p class="text-muted-foreground text-center py-4">No participants</p>`);
var root_8 = $.from_html(`<!> <!>`, 1);
var root_17 = $.from_html(`<button class="text-xs text-muted-foreground hover:text-foreground underline disabled:opacity-50">Group with...</button>`);
var root_18 = $.from_html(`<button class="text-xs text-muted-foreground hover:text-foreground underline disabled:opacity-50">Ungroup</button>`);
var root_16 = $.from_html(`<div class="mt-2 flex gap-3"><!></div>`);
var root_21 = $.from_html(`<p class="text-sm text-red-600"> </p>`);
var root_22 = $.from_html(`<p class="text-sm text-green-600"> </p>`);
var root_20 = $.from_html(`<div class="mt-3 pt-3 border-t border-border space-y-1"><!> <!></div>`);
var root_15 = $.from_html(`<div class="flex items-start justify-between"><div class="flex-1"><span class="font-semibold text-lg"> </span> <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm mt-1"><span class="text-muted-foreground">Paid: <span class="font-medium text-foreground"> </span></span> <span class="text-muted-foreground">Cost: <span class="font-medium text-foreground"> </span></span></div></div> <div class="text-right"><span> </span></div></div> <!> <!>`, 1);
var root_7 = $.from_html(`<!> <div class="w-full space-y-3"></div>`, 1);
var root_26 = $.from_html(`<option> </option>`);
var root_25 = $.from_html(`<div class="fixed inset-0 z-50 bg-black/50" role="presentation"></div> <div role="dialog" aria-modal="true" aria-label="Group with" class="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 bg-background rounded-lg shadow-lg p-6 max-w-sm mx-auto"><h2 class="text-lg font-semibold mb-4"> </h2> <label for="group-modal-select" class="text-sm text-muted-foreground block mb-1">Partner</label> <select id="group-modal-select" class="w-full text-sm border border-input rounded-md px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring mb-6"><option>(none)</option><!></select> <div class="flex gap-3 justify-end"><!> <!></div></div>`, 1);
var root = $.from_html(`<div class="flex flex-col items-center space-y-4 w-full max-w-[520px] mx-auto"><!> <!></div> <!>`, 1);

export default function Settlement($$anchor, $$props) {
	$.push($$props, true);

	// Settlement Page - Balances & Reimbursement Proposals
	const splitId = $.derived(() => route.params.splitId || '');

	// State
	let settlement = $.state(null);

	let split = $.state(null);
	let splitName = $.state($.proxy(getCachedSplitName(route.params.splitId || '')));
	let isLoading = $.state(true);
	let showLoading = $.state(false);
	let loadingTimer = null;
	let showReimbursements = $.state(false);
	let isUnsettling = $.state(false);
	let isUpdatingGroup = $.state(false);
	let groupModalParticipantId = $.state(null);
	let groupModalPartnerId = $.state('');

	// standard only: for "Group with" modal
	// group only: for Ungroup action
	// Collapsed balance list: shared-account members are merged into one group entry.
	const displayBalances = $.derived(() => (() => {
		if (!$.get(split) || !$.get(settlement)) return [];

		const seen = new Set();
		const result = [];

		for (const p of $.get(split).participants) {
			if (seen.has(p.id)) continue;

			if (!p.sharedAccountId) {
				const b = $.get(settlement).balances.find((b) => b.participantId === p.id);

				if (!b) {
					seen.add(p.id);

					continue;
				}

				result.push({
					key: p.id,
					displayName: p.name,
					totalPaid: b.totalPaid,
					totalCost: b.totalCost,
					balance: b.balance,
					type: 'standard',
					matchId: p.id,
					participantId: p.id
				});

				seen.add(p.id);
			} else {
				const members = $.get(split).participants.filter((m) => m.sharedAccountId === p.sharedAccountId);

				members.forEach((m) => seen.add(m.id));

				const memberBalances = members.map((m) => $.get(settlement).balances.find((b) => b.participantId === m.id)).filter((b) => b != null);
				const displayName = [...members].sort((a, b) => a.name.localeCompare(b.name)).map((m) => m.name).join(' & ');

				result.push({
					key: p.sharedAccountId,
					displayName,
					totalPaid: memberBalances.reduce((s, b) => s + b.totalPaid, 0),
					totalCost: memberBalances.reduce((s, b) => s + b.totalCost, 0),
					balance: memberBalances.reduce((s, b) => s + b.balance, 0),
					type: 'group',
					matchId: p.sharedAccountId,
					memberIds: members.map((m) => m.id)
				});
			}
		}

		return result;
	})());

	const groupModalParticipant = $.derived(() => $.get(groupModalParticipantId)
		? $.get(split)?.participants.find((p) => p.id === $.get(groupModalParticipantId)) ?? null
		: null);

	// Modal options: standard participants shown individually, existing groups shown as one entry.
	const groupModalOptions = $.derived(() => (() => {
		if (!$.get(groupModalParticipantId) || !$.get(split)) return [];

		const seen = new Set();
		const result = [];

		for (const p of $.get(split).participants) {
			if (p.id === $.get(groupModalParticipantId) || seen.has(p.id)) continue;

			if (!p.sharedAccountId) {
				result.push({ id: p.id, displayName: p.name });
				seen.add(p.id);
			} else {
				const members = $.get(split).participants.filter((m) => m.sharedAccountId === p.sharedAccountId);

				members.forEach((m) => seen.add(m.id));

				const displayName = [...members].sort((a, b) => a.name.localeCompare(b.name)).map((m) => m.name).join(' & ');

				result.push({ id: members[0].id, displayName });
			}
		}

		return result;
	})());

	// On mount: load the split and derive balances from participant computed fields.
	// Never POST on load — that would persist a settlement and undo an intentional unsettle.
	// If a settlement is already persisted, fetch its reimbursements and show them directly.
	$.user_effect(() => {
		if ($.get(splitId)) {
			loadSettlement($.get(splitId));
		}
	});

	async function loadSettlement(id) {
		$.set(isLoading, true);
		$.set(showLoading, false);
		$.set(settlement, null);
		$.set(showReimbursements, false);

		if (loadingTimer) clearTimeout(loadingTimer);

		loadingTimer = setTimeout(
			() => {
				$.set(showLoading, true);
			},
			500
		);

		try {
			const splitData = await getSplit(id);

			$.set(split, splitData, true);
			$.set(splitName, splitData.name, true);
			setCachedSplitName(id, splitData.name);

			// Derive balances from participant computed fields (always available, no POST needed).
			const balances = splitData.participants.map((p) => ({
				participantId: p.id,
				participantName: p.name,
				totalPaid: p.totalPaid ?? 0,
				totalCost: p.totalCost ?? 0,
				balance: p.balance ?? 0
			}));

			if (splitData.settlement != null) {
				// Already resolved — fetch persisted reimbursements and show them directly.
				const persisted = await getSettlement(id);

				$.set(settlement, { balances, reimbursements: persisted.reimbursements }, true);
				$.set(showReimbursements, true);
			} else {
				$.set(settlement, { balances, reimbursements: [] }, true);
			}
		} catch(err) {
			const apiError = err;

			addToast({
				type: 'error',
				message: apiError.detail || 'Failed to load settlement'
			});
		} finally {
			$.set(isLoading, false);
			$.set(showLoading, false);

			if (loadingTimer) {
				clearTimeout(loadingTimer);
				loadingTimer = null;
			}
		}
	}

	async function handleResolve() {
		try {
			const settlementData = await resolveSettlement($.get(splitId));

			$.set(settlement, settlementData, true);
			$.set(showReimbursements, true);
		} catch(err) {
			addToast({ type: 'error', message: err.detail || 'Failed to resolve' });
		}
	}

	function formatCurrency(amount) {
		return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(amount);
	}

	function balanceColorClass(balance) {
		if (balance > 0.005) return 'text-green-600';
		if (balance < -0.005) return 'text-red-600';

		return 'text-muted-foreground';
	}

	function balanceLabel(balance) {
		if (balance > 0.005) return `Owed ${formatCurrency(balance)}`;
		if (balance < -0.005) return `Owes ${formatCurrency(Math.abs(balance))}`;

		return 'Settled';
	}

	function formatSettlementText(url) {
		if (!$.get(split) || !$.get(settlement)) return '';

		const totalExpenses = $.get(split).expenses.reduce((sum, e) => sum + e.amount, 0);
		const expenseCount = $.get(split).expenses.length;
		const participantCount = $.get(split).participants.length;
		const sorted = [...$.get(settlement).reimbursements].sort((a, b) => a.fromName.localeCompare(b.fromName));

		const lines = [
			`=== ${$.get(split).name} ===`,
			`${expenseCount} expense${expenseCount !== 1 ? 's' : ''} — Total: ${formatCurrency(totalExpenses)}`,
			`${participantCount} participant${participantCount !== 1 ? 's' : ''}`
		];

		if (sorted.length > 0) {
			lines.push('');
			lines.push('Settlements:');

			for (const r of sorted) {
				lines.push(`${r.fromName} → ${r.toName}: ${formatCurrency(r.amount)}`);
			}

			// Group by receiver to build summary
			const byReceiver = new Map();

			for (const r of sorted) {
				if (!byReceiver.has(r.toId)) {
					byReceiver.set(r.toId, { name: r.toName, amounts: [] });
				}

				byReceiver.get(r.toId).amounts.push(r.amount);
			}

			lines.push('');
			lines.push('Summary:');

			for (const { name, amounts } of byReceiver.values()) {
				if (amounts.length === 1) {
					lines.push(`${name} receives ${formatCurrency(amounts[0])}`);
				} else {
					const total = amounts.reduce((s, a) => s + a, 0);

					lines.push(`${name} receives ${amounts.map(formatCurrency).join(' + ')} = ${formatCurrency(total)}`);
				}
			}
		} else {
			lines.push('');
			lines.push('All settled — no transfers needed!');
		}

		lines.push('');
		lines.push(url);

		return lines.join('\n');
	}

	async function handleExportSettlement() {
		const url = typeof window !== 'undefined' ? window.location.href : '';
		const text = formatSettlementText(url);

		if (!navigator.clipboard) {
			addToast({ type: 'info', message: text });

			return;
		}

		try {
			await navigator.clipboard.writeText(text);
			addToast({ type: 'success', message: 'Settlement copied to clipboard!' });
		} catch {
			addToast({ type: 'info', message: text });
		}
	}

	function generateSharedAccountId() {
		const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
		const bytes = crypto.getRandomValues(new Uint8Array(21));

		return Array.from(bytes, (b) => alphabet[b & 63]).join('');
	}

	async function handleSharedAccountChange(participantId, newPartnerIdOrEmpty) {
		if (!$.get(split)) return;

		const participant = $.get(split).participants.find((p) => p.id === participantId);

		if (!participant) return;

		$.set(isUpdatingGroup, true);

		try {
			if (!newPartnerIdOrEmpty) {
				// Ungroup: clear shared account
				await updateParticipant($.get(splitId), participantId, {
					name: participant.name,
					nights: participant.nights,
					share: participant.share,
					sharedAccountId: null
				});
			} else {
				const partner = $.get(split).participants.find((p) => p.id === newPartnerIdOrEmpty);

				if (!partner) return;

				if (partner.sharedAccountId) {
					// Join partner's existing group
					await updateParticipant($.get(splitId), participantId, {
						name: participant.name,
						nights: participant.nights,
						share: participant.share,
						sharedAccountId: partner.sharedAccountId
					});
				} else {
					// Create new group for both
					const newGroupId = generateSharedAccountId();

					await updateParticipant($.get(splitId), participantId, {
						name: participant.name,
						nights: participant.nights,
						share: participant.share,
						sharedAccountId: newGroupId
					});

					await updateParticipant($.get(splitId), newPartnerIdOrEmpty, {
						name: partner.name,
						nights: partner.nights,
						share: partner.share,
						sharedAccountId: newGroupId
					});
				}
			}

			// Reload split and recompute balances from fresh participant data
			const updatedSplit = await getSplit($.get(splitId));

			$.set(split, updatedSplit, true);

			const balances = updatedSplit.participants.map((p) => ({
				participantId: p.id,
				participantName: p.name,
				totalPaid: p.totalPaid ?? 0,
				totalCost: p.totalCost ?? 0,
				balance: p.balance ?? 0
			}));

			$.set(settlement, { ...$.get(settlement), balances }, true);
		} catch(err) {
			addToast({
				type: 'error',
				message: err.detail || 'Failed to update group'
			});
		} finally {
			$.set(isUpdatingGroup, false);
		}
	}

	async function handleUngroup(memberIds) {
		if (!$.get(split)) return;

		$.set(isUpdatingGroup, true);

		try {
			for (const memberId of memberIds) {
				const p = $.get(split).participants.find((m) => m.id === memberId);

				if (!p) continue;

				await updateParticipant($.get(splitId), memberId, {
					name: p.name,
					nights: p.nights,
					share: p.share,
					sharedAccountId: null
				});
			}

			const updatedSplit = await getSplit($.get(splitId));

			$.set(split, updatedSplit, true);

			const balances = updatedSplit.participants.map((p) => ({
				participantId: p.id,
				participantName: p.name,
				totalPaid: p.totalPaid ?? 0,
				totalCost: p.totalCost ?? 0,
				balance: p.balance ?? 0
			}));

			$.set(settlement, { ...$.get(settlement), balances }, true);
		} catch(err) {
			addToast({ type: 'error', message: err.detail || 'Failed to ungroup' });
		} finally {
			$.set(isUpdatingGroup, false);
		}
	}

	async function handleUnsettle() {
		$.set(isUnsettling, true);

		try {
			await unsettleSettlement($.get(splitId));
			$.set(showReimbursements, false);
		} catch(err) {
			addToast({ type: 'error', message: err.detail || 'Failed to unsettle' });
		} finally {
			$.set(isUnsettling, false);
		}
	}

	var fragment = root();
	var div = $.first_child(fragment);
	var node = $.child(div);

	{
		var consequent = ($$anchor) => {
			SplitPageHeader($$anchor, {
				get splitName() {
					return $.get(splitName);
				},

				get splitId() {
					return $.get(splitId);
				}
			});
		};

		$.if(node, ($$render) => {
			if ($.get(splitId)) $$render(consequent);
		});
	}

	var node_1 = $.sibling(node, 2);

	{
		var consequent_1 = ($$anchor) => {
			var div_1 = root_2();

			$.append($$anchor, div_1);
		};

		var alternate_4 = ($$anchor) => {
			var fragment_2 = $.comment();
			var node_2 = $.first_child(fragment_2);

			{
				var consequent_9 = ($$anchor) => {
					var fragment_3 = $.comment();
					var node_3 = $.first_child(fragment_3);

					{
						var consequent_8 = ($$anchor) => {
							var fragment_4 = $.comment();
							var node_4 = $.first_child(fragment_4);

							{
								var consequent_2 = ($$anchor) => {
									var p_1 = root_6();

									$.append($$anchor, p_1);
								};

								var alternate_2 = ($$anchor) => {
									var fragment_5 = root_7();
									var node_5 = $.first_child(fragment_5);

									{
										var consequent_3 = ($$anchor) => {
											var fragment_6 = root_8();
											var node_6 = $.first_child(fragment_6);

											Button(node_6, {
												onclick: handleExportSettlement,
												variant: 'outline',
												class: 'w-full min-h-[44px]',

												children: ($$anchor, $$slotProps) => {
													$.next();

													var text_1 = $.text('Export Settlement');

													$.append($$anchor, text_1);
												},

												$$slots: { default: true }
											});

											var node_7 = $.sibling(node_6, 2);

											Button(node_7, {
												onclick: handleUnsettle,

												get disabled() {
													return $.get(isUnsettling);
												},

												variant: 'ghost',
												class: 'w-full min-h-[44px] text-muted-foreground',

												children: ($$anchor, $$slotProps) => {
													$.next();

													var text_2 = $.text();

													$.template_effect(() => $.set_text(text_2, $.get(isUnsettling) ? 'Unsettling...' : 'Unsettle'));
													$.append($$anchor, text_2);
												},

												$$slots: { default: true }
											});

											$.append($$anchor, fragment_6);
										};

										var alternate = ($$anchor) => {
											Button($$anchor, {
												onclick: handleResolve,
												class: 'w-full min-h-[44px]',

												children: ($$anchor, $$slotProps) => {
													$.next();

													var text_3 = $.text('Resolve');

													$.append($$anchor, text_3);
												},

												$$slots: { default: true }
											});
										};

										$.if(node_5, ($$render) => {
											if ($.get(showReimbursements)) $$render(consequent_3); else $$render(alternate, false);
										});
									}

									var div_2 = $.sibling(node_5, 2);

									$.each(div_2, 21, () => $.get(displayBalances), (entry) => entry.key, ($$anchor, entry) => {
										var fragment_9 = $.comment();
										var node_8 = $.first_child(fragment_9);

										$.component(node_8, () => Card.Root, ($$anchor, Card_Root) => {
											Card_Root($$anchor, {
												class: 'w-full',

												children: ($$anchor, $$slotProps) => {
													var fragment_10 = $.comment();
													var node_9 = $.first_child(fragment_10);

													$.component(node_9, () => Card.Content, ($$anchor, Card_Content) => {
														Card_Content($$anchor, {
															class: 'py-4',

															children: ($$anchor, $$slotProps) => {
																var fragment_11 = root_15();
																var div_3 = $.first_child(fragment_11);
																var div_4 = $.child(div_3);
																var span = $.child(div_4);
																var text_4 = $.child(span, true);

																$.reset(span);

																var div_5 = $.sibling(span, 2);
																var span_1 = $.child(div_5);
																var span_2 = $.sibling($.child(span_1));
																var text_5 = $.child(span_2, true);

																$.reset(span_2);
																$.reset(span_1);

																var span_3 = $.sibling(span_1, 2);
																var span_4 = $.sibling($.child(span_3));
																var text_6 = $.child(span_4, true);

																$.reset(span_4);
																$.reset(span_3);
																$.reset(div_5);
																$.reset(div_4);

																var div_6 = $.sibling(div_4, 2);
																var span_5 = $.child(div_6);
																var text_7 = $.child(span_5, true);

																$.reset(span_5);
																$.reset(div_6);
																$.reset(div_3);

																var node_10 = $.sibling(div_3, 2);

																{
																	var consequent_5 = ($$anchor) => {
																		var div_7 = root_16();
																		var node_11 = $.child(div_7);

																		{
																			var consequent_4 = ($$anchor) => {
																				var button = root_17();

																				button.__click = () => {
																					$.set(groupModalParticipantId, $.get(entry).participantId, true);
																					$.set(groupModalPartnerId, '');
																				};

																				$.template_effect(() => button.disabled = $.get(isUpdatingGroup));
																				$.append($$anchor, button);
																			};

																			var alternate_1 = ($$anchor) => {
																				var button_1 = root_18();

																				button_1.__click = () => handleUngroup($.get(entry).memberIds);
																				$.template_effect(() => button_1.disabled = $.get(isUpdatingGroup));
																				$.append($$anchor, button_1);
																			};

																			$.if(node_11, ($$render) => {
																				if ($.get(entry).type === 'standard') $$render(consequent_4); else $$render(alternate_1, false);
																			});
																		}

																		$.reset(div_7);
																		$.append($$anchor, div_7);
																	};

																	$.if(node_10, ($$render) => {
																		if (!$.get(showReimbursements)) $$render(consequent_5);
																	});
																}

																var node_12 = $.sibling(node_10, 2);

																{
																	var consequent_7 = ($$anchor) => {
																		const outgoing = $.derived(() => $.get(settlement).reimbursements.filter((r) => r.fromId === $.get(entry).matchId));
																		const incoming = $.derived(() => $.get(settlement).reimbursements.filter((r) => r.toId === $.get(entry).matchId));
																		var fragment_12 = $.comment();
																		var node_13 = $.first_child(fragment_12);

																		{
																			var consequent_6 = ($$anchor) => {
																				var div_8 = root_20();
																				var node_14 = $.child(div_8);

																				$.each(node_14, 17, () => $.get(outgoing), $.index, ($$anchor, r) => {
																					var p_2 = root_21();
																					var text_8 = $.child(p_2);

																					$.reset(p_2);
																					$.template_effect(($0) => $.set_text(text_8, `Pay ${$0 ?? ''} to ${$.get(r).toName ?? ''}`), [() => formatCurrency($.get(r).amount)]);
																					$.append($$anchor, p_2);
																				});

																				var node_15 = $.sibling(node_14, 2);

																				$.each(node_15, 17, () => $.get(incoming), $.index, ($$anchor, r) => {
																					var p_3 = root_22();
																					var text_9 = $.child(p_3);

																					$.reset(p_3);
																					$.template_effect(($0) => $.set_text(text_9, `Receive ${$0 ?? ''} from ${$.get(r).fromName ?? ''}`), [() => formatCurrency($.get(r).amount)]);
																					$.append($$anchor, p_3);
																				});

																				$.reset(div_8);
																				$.append($$anchor, div_8);
																			};

																			$.if(node_13, ($$render) => {
																				if ($.get(outgoing).length > 0 || $.get(incoming).length > 0) $$render(consequent_6);
																			});
																		}

																		$.append($$anchor, fragment_12);
																	};

																	$.if(node_12, ($$render) => {
																		if ($.get(showReimbursements)) $$render(consequent_7);
																	});
																}

																$.template_effect(
																	($0, $1, $2, $3) => {
																		$.set_text(text_4, $.get(entry).displayName);
																		$.set_text(text_5, $0);
																		$.set_text(text_6, $1);
																		$.set_class(span_5, 1, `text-sm font-medium ${$2 ?? ''}`);
																		$.set_text(text_7, $3);
																	},
																	[
																		() => formatCurrency($.get(entry).totalPaid),
																		() => formatCurrency($.get(entry).totalCost),
																		() => balanceColorClass($.get(entry).balance),
																		() => balanceLabel($.get(entry).balance)
																	]
																);

																$.append($$anchor, fragment_11);
															},

															$$slots: { default: true }
														});
													});

													$.append($$anchor, fragment_10);
												},

												$$slots: { default: true }
											});
										});

										$.append($$anchor, fragment_9);
									});

									$.reset(div_2);
									$.append($$anchor, fragment_5);
								};

								$.if(node_4, ($$render) => {
									if ($.get(displayBalances).length === 0) $$render(consequent_2); else $$render(alternate_2, false);
								});
							}

							$.append($$anchor, fragment_4);
						};

						var alternate_3 = ($$anchor) => {
							Button($$anchor, {
								onclick: handleResolve,
								class: 'w-full min-h-[44px]',

								children: ($$anchor, $$slotProps) => {
									$.next();

									var text_10 = $.text('Resolve');

									$.append($$anchor, text_10);
								},

								$$slots: { default: true }
							});
						};

						$.if(node_3, ($$render) => {
							if ($.get(settlement)) $$render(consequent_8); else $$render(alternate_3, false);
						});
					}

					$.append($$anchor, fragment_3);
				};

				$.if(
					node_2,
					($$render) => {
						if (!$.get(isLoading) && $.get(split)) $$render(consequent_9);
					},
					true
				);
			}

			$.append($$anchor, fragment_2);
		};

		$.if(node_1, ($$render) => {
			if ($.get(showLoading)) $$render(consequent_1); else $$render(alternate_4, false);
		});
	}

	$.reset(div);

	var node_16 = $.sibling(div, 2);

	{
		var consequent_10 = ($$anchor) => {
			var fragment_14 = root_25();
			var div_9 = $.first_child(fragment_14);

			div_9.__click = () => $.set(groupModalParticipantId, null);

			var div_10 = $.sibling(div_9, 2);
			var h2 = $.child(div_10);
			var text_11 = $.child(h2);

			$.reset(h2);

			var select = $.sibling(h2, 4);
			var option = $.child(select);

			option.value = option.__value = '';

			var node_17 = $.sibling(option);

			$.each(node_17, 17, () => $.get(groupModalOptions), (opt) => opt.id, ($$anchor, opt) => {
				var option_1 = root_26();
				var text_12 = $.child(option_1, true);

				$.reset(option_1);

				var option_1_value = {};

				$.template_effect(() => {
					$.set_text(text_12, $.get(opt).displayName);

					if (option_1_value !== (option_1_value = $.get(opt).id)) {
						option_1.value = (option_1.__value = $.get(opt).id) ?? '';
					}
				});

				$.append($$anchor, option_1);
			});

			$.reset(select);

			var div_11 = $.sibling(select, 2);
			var node_18 = $.child(div_11);

			Button(node_18, {
				variant: 'ghost',
				onclick: () => $.set(groupModalParticipantId, null),

				children: ($$anchor, $$slotProps) => {
					$.next();

					var text_13 = $.text('Cancel');

					$.append($$anchor, text_13);
				},

				$$slots: { default: true }
			});

			var node_19 = $.sibling(node_18, 2);

			Button(node_19, {
				onclick: async () => {
					const pid = $.get(groupModalParticipantId);
					const partner = $.get(groupModalPartnerId);

					$.set(groupModalParticipantId, null);
					await handleSharedAccountChange(pid, partner);
				},

				children: ($$anchor, $$slotProps) => {
					$.next();

					var text_14 = $.text('OK');

					$.append($$anchor, text_14);
				},

				$$slots: { default: true }
			});

			$.reset(div_11);
			$.reset(div_10);
			$.template_effect(() => $.set_text(text_11, `Group ${$.get(groupModalParticipant)?.name ?? ''} with`));
			$.bind_select_value(select, () => $.get(groupModalPartnerId), ($$value) => $.set(groupModalPartnerId, $$value));
			$.append($$anchor, fragment_14);
		};

		$.if(node_16, ($$render) => {
			if ($.get(groupModalParticipantId)) $$render(consequent_10);
		});
	}

	$.append($$anchor, fragment);
	$.pop();
}

$.delegate(['click']);
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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